import { useEffect, useState, useCallback } from "react";
import { SunIcon, MoonIcon, ComputerDesktopIcon } from "@heroicons/react/24/outline";

export default function DarkModeToggle() {
  const [theme, setTheme] = useState(
    localStorage.getItem("theme") || "system"
  );
  
  const element = document.documentElement;
  const darkQuery = window.matchMedia("(prefers-color-scheme: dark)");

  const onWindowMatch = useCallback(() => {
    if (theme === "system") {
      if (darkQuery.matches) {
        element.classList.add("dark");
      } else {
        element.classList.remove("dark");
      }
    }
  }, [darkQuery.matches, element.classList, theme]);

  useEffect(() => {
    switch (theme) {
      case "dark":
        element.classList.add("dark");
        localStorage.setItem("theme", "dark");
        break;
      case "light":
        element.classList.remove("dark");
        localStorage.setItem("theme", "light");
        break;
      case "system":
        localStorage.setItem("theme", "system");
        onWindowMatch();
        break;
      default:
        break;
    }
  }, [theme, element.classList, onWindowMatch]);

  useEffect(() => {
    const handleSystemThemeChange = () => {
      if (theme === "system") {
        onWindowMatch();
      }
    };

    darkQuery.addEventListener("change", handleSystemThemeChange);
    return () => darkQuery.removeEventListener("change", handleSystemThemeChange);
  }, [darkQuery, onWindowMatch, theme]);

  useEffect(() => {
    onWindowMatch();
  }, [onWindowMatch]);

  const options = [
    {
      icon: SunIcon,
      text: "light",
      tooltip: "Modo claro"
    },
    {
      icon: MoonIcon,
      text: "dark",
      tooltip: "Modo oscuro"
    },
    {
      icon: ComputerDesktopIcon,
      text: "system",
      tooltip: "Usar configuración del sistema"
    }
  ];

  return (
    <div className="absolute top-2 right-2 z-50 sm:top-4 sm:right-4" role="group" aria-label="Selector de tema">
      <div className="p-1 flex gap-0.5
        bg-[#faf8f4] dark:bg-neutral-900
        border border-stone-200 dark:border-stone-700
        rounded-sm shadow-sm">
        {options?.map((option) => (
          <button
            key={option.text}
            onClick={() => setTheme(option.text)}
            className={`
              w-11 h-11
              flex items-center justify-center
              rounded-sm transition-colors duration-200 motion-reduce:transition-none
              focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#315c7a] dark:focus-visible:outline-[#84a9be]
              ${theme === option.text 
                ? 'bg-stone-200/70 dark:bg-neutral-800 text-[#315c7a] dark:text-[#84a9be]'
                : 'text-stone-600 dark:text-stone-400 hover:bg-stone-200/50 hover:text-stone-950 dark:hover:bg-neutral-800 dark:hover:text-stone-100'
              }
            `}
            aria-label={option.tooltip}
            aria-pressed={theme === option.text}
          >
            <option.icon className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />
          </button>
        ))}
      </div>
    </div>
  );
}
